
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AnthropicModel {
  id: string;
  name: string;
  description?: string;
  context_window: number;
  created?: number;
}

export interface AnthropicMessage {
  role: 'user' | 'assistant' | 'system';
  content: string | AnthropicContentBlock[];
}

export interface AnthropicContentBlock {
  type: 'text' | 'image';
  text?: string;
  source?: {
    type: 'base64';
    media_type: string;
    data: string;
  };
}

export interface AnthropicTool {
  name: string;
  description?: string;
  input_schema: Record<string, any>;
}

export interface AnthropicCompletionOptions {
  model?: string;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
  tools?: AnthropicTool[];
}

/**
 * Fetch available Anthropic models
 */
export const fetchAnthropicModels = async (): Promise<AnthropicModel[]> => {
  try {
    // For now, return a static list of models since Anthropic doesn't have a models endpoint
    // We can update this in the future if needed
    return [
      {
        id: 'claude-3-5-sonnet-latest',
        name: 'Claude 3.5 Sonnet',
        description: 'The latest Claude 3.5 Sonnet model',
        context_window: 200000
      },
      {
        id: 'claude-3-opus-latest',
        name: 'Claude 3 Opus',
        description: 'The latest Claude 3 Opus model',
        context_window: 200000
      },
      {
        id: 'claude-3-sonnet-latest',
        name: 'Claude 3 Sonnet',
        description: 'The latest Claude 3 Sonnet model', 
        context_window: 200000
      },
      {
        id: 'claude-3-haiku-latest',
        name: 'Claude 3 Haiku',
        description: 'The latest Claude 3 Haiku model',
        context_window: 200000
      }
    ];
  } catch (error) {
    console.error('Error in fetchAnthropicModels:', error);
    toast.error('Failed to fetch Anthropic models');
    throw error;
  }
};

/**
 * Generate a chat completion using Anthropic
 */
export const generateAnthropicCompletion = async (
  messages: AnthropicMessage[],
  options: AnthropicCompletionOptions = {}
) => {
  try {
    const requestBody: Record<string, any> = {
      messages,
      model: options.model || 'claude-3-5-sonnet-latest',
      temperature: options.temperature || 0.7,
      max_tokens: options.max_tokens || 1024,
      stream: options.stream || false,
    };
    
    // Add tools if provided
    if (options.tools && options.tools.length > 0) {
      requestBody.tools = options.tools;
    }
    
    const { data, error } = await supabase.functions.invoke('anthropic-chat-completion', {
      body: requestBody,
    });
    
    if (error) {
      console.error('Error generating Anthropic completion:', error);
      toast.error('Failed to generate Anthropic completion');
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in generateAnthropicCompletion:', error);
    toast.error('Failed to generate Anthropic completion');
    throw error;
  }
};

/**
 * Stream a chat completion using Anthropic
 */
export const streamAnthropicCompletion = async (
  messages: AnthropicMessage[],
  options: AnthropicCompletionOptions = {}
) => {
  try {
    // Properly construct the edge function URL
    const functionPath = 'anthropic-chat-completion';
    const supabaseUrl = "https://dysiqkicxwiusdzijtct.supabase.co";
    const functionUrl = `${supabaseUrl}/functions/v1/${functionPath}`;
    
    // Get the current session
    const sessionResponse = await supabase.auth.getSession();
    const session = sessionResponse.data.session;
    
    const requestBody: Record<string, any> = {
      messages,
      model: options.model || 'claude-3-5-sonnet-latest',
      temperature: options.temperature || 0.7,
      max_tokens: options.max_tokens || 1024,
      stream: true,
    };
    
    // Add tools if provided
    if (options.tools && options.tools.length > 0) {
      requestBody.tools = options.tools;
    }
    
    const response = await fetch(
      functionUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify(requestBody),
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error streaming Anthropic completion:', errorText);
      toast.error('Failed to stream Anthropic completion');
      throw new Error(errorText);
    }
    
    return response.body;
  } catch (error) {
    console.error('Error in streamAnthropicCompletion:', error);
    toast.error('Failed to stream Anthropic completion');
    throw error;
  }
};

/**
 * Execute a capability through Anthropic function calling
 */
export const executeCapabilityWithAnthropic = async (
  systemPrompt: string,
  userPrompt: string,
  capability: AnthropicTool,
  options: Omit<AnthropicCompletionOptions, 'tools'> = {}
) => {
  try {
    const messages: AnthropicMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ];

    const result = await generateAnthropicCompletion(messages, {
      ...options,
      tools: [capability]
    });

    // Extract and process the tool call
    const toolCalls = result.tool_use;
    if (toolCalls) {
      // Parse the arguments from the tool call
      const args = toolCalls.input;
      
      // Execute the capability function via edge function
      const { data, error } = await supabase.functions.invoke('execute-capability', {
        body: {
          capability: capability.name,
          arguments: args
        }
      });
      
      if (error) throw error;
      
      // Send the tool response back to the model
      const toolResponse: AnthropicMessage = {
        role: 'assistant',
        content: `I've processed your request with the ${capability.name} tool and got this result: ${JSON.stringify(data)}`
      };
      
      // Get the final response incorporating the tool result
      const finalMessages: AnthropicMessage[] = [
        ...messages, 
        toolResponse, 
        { role: 'user', content: 'Can you explain these results to me?' }
      ];
      
      const finalResult = await generateAnthropicCompletion(finalMessages, options);
      
      return {
        initialResponse: result,
        toolExecution: data,
        finalResponse: finalResult
      };
    }
    
    // If no tool call was made, just return the initial response
    return { initialResponse: result };
  } catch (error) {
    console.error('Error executing capability with Anthropic:', error);
    toast.error('Failed to execute capability with Anthropic');
    throw error;
  }
};
